import { useState, useEffect } from 'react';
import axios from 'axios';
import { Package, Plus, Trash2, Edit2, TrendingUp, DollarSign, BarChart3, X, Save, Percent, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const PRODUCT_TYPES = [
  'Poster', 'Canvas', 'Acrylic Print', 'Shirt', 'Frame', 'Trading Card', 'Other'
];

const CANVAS_SIZES = ['24x18', '32x24', '40x30', '48x36'];
const SHIRT_SIZES = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL'];

// Default cost per item by product type (can be overridden)
const DEFAULT_COSTS = {
  'Poster': 5,
  'Canvas': 25,
  'Acrylic Print': 20,
  'Shirt': 12,
  'Frame': 8,
  'Trading Card': 0.50,
  'Other': 0
};

export default function ProductSales() {
  const [sales, setSales] = useState([]);
  const [events, setEvents] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('sales'); // 'sales', 'analytics', or 'topSellers'
  const [selectedEvent, setSelectedEvent] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    event_id: '',
    product_type: '',
    product_name: '',
    size: '',
    quantity: 1,
    unit_price: '',
    discount_amount: '',
    cost_per_item: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, [selectedEvent]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('vyrus_token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [salesRes, eventsRes, analyticsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/product-sales${selectedEvent ? `?event_id=${selectedEvent}` : ''}`, { headers }),
        axios.get(`${BACKEND_URL}/api/events`, { headers }),
        axios.get(`${BACKEND_URL}/api/product-sales/analytics${selectedEvent ? `?event_id=${selectedEvent}` : ''}`, { headers })
      ]);
      
      setSales(salesRes.data);
      setEvents(eventsRes.data);
      setAnalytics(analyticsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.event_id || !formData.product_type || !formData.unit_price) {
      toast.error('Please fill in required fields');
      return;
    }

    const total_price = parseFloat(formData.unit_price) * parseInt(formData.quantity);
    const payload = {
      ...formData,
      unit_price: parseFloat(formData.unit_price),
      quantity: parseInt(formData.quantity),
      total_price,
      discount_amount: parseFloat(formData.discount_amount) || 0,
      cost_per_item: parseFloat(formData.cost_per_item) || 0
    };

    try {
      const token = localStorage.getItem('vyrus_token');
      if (editingSale) {
        await axios.put(`${BACKEND_URL}/api/product-sales/${editingSale.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Sale updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/product-sales`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Sale recorded');
      }
      
      setShowForm(false);
      setEditingSale(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to save sale');
    }
  };

  const handleDelete = async (saleId) => {
    if (!window.confirm('Delete this sale record?')) return;
    
    try {
      const token = localStorage.getItem('vyrus_token');
      await axios.delete(`${BACKEND_URL}/api/product-sales/${saleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Sale deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleEdit = (sale) => {
    setEditingSale(sale);
    setFormData({
      event_id: sale.event_id,
      product_type: sale.product_type,
      product_name: sale.product_name || '',
      size: sale.size || '',
      quantity: sale.quantity,
      unit_price: sale.unit_price,
      discount_amount: sale.discount_amount || '',
      cost_per_item: sale.cost_per_item || '',
      notes: sale.notes || ''
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      event_id: selectedEvent || '',
      product_type: '',
      product_name: '',
      size: '',
      quantity: 1,
      unit_price: '',
      discount_amount: '',
      cost_per_item: '',
      notes: ''
    });
  };

  // Auto-fill default cost when product type changes
  const handleProductTypeChange = (type) => {
    const defaultCost = DEFAULT_COSTS[type] || 0;
    setFormData({
      ...formData,
      product_type: type,
      size: '',
      cost_per_item: defaultCost.toString()
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const getEventName = (eventId) => {
    const event = events.find(e => e.id === eventId);
    return event?.name || 'Unknown Event';
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-muted-foreground">Loading...</div></div>;
  }

  return (
    <div className="space-y-4 sm:space-y-6 pb-20">
      <div>
        <h1 className="text-2xl sm:text-4xl font-bold font-heading tracking-tight" data-testid="sales-accounting-title">Sales Accounting</h1>
        <p className="text-muted-foreground mt-1 text-sm">Track product sales with discounts, costs, and profit analytics</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-border pb-2 overflow-x-auto">
        <Button variant={activeTab === 'sales' ? 'default' : 'ghost'} onClick={() => setActiveTab('sales')} className="flex items-center gap-2">
          <Package className="h-4 w-4" /> Sales
        </Button>
        <Button variant={activeTab === 'analytics' ? 'default' : 'ghost'} onClick={() => setActiveTab('analytics')} className="flex items-center gap-2">
          <BarChart3 className="h-4 w-4" /> Analytics
        </Button>
        <Button variant={activeTab === 'topSellers' ? 'default' : 'ghost'} onClick={() => setActiveTab('topSellers')} className="flex items-center gap-2">
          <TrendingUp className="h-4 w-4" /> Top Sellers
        </Button>
      </div>

      {/* Event Filter */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
        <select
          value={selectedEvent}
          onChange={(e) => setSelectedEvent(e.target.value)}
          className="px-4 py-2 rounded-lg bg-secondary/50 border border-transparent focus:border-primary text-foreground"
        >
          <option value="">All Events</option>
          {events.map(event => (
            <option key={event.id} value={event.id}>{event.name}</option>
          ))}
        </select>
        
        <Button onClick={() => { resetForm(); setShowForm(true); setEditingSale(null); }} className="ml-auto">
          <Plus className="h-4 w-4 mr-2" /> Record Sale
        </Button>
      </div>

      {activeTab === 'sales' ? (
        /* Sales List */
        <div className="space-y-3">
          {sales.length === 0 ? (
            <Card className="p-8 text-center bg-card border-border/50">
              <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No sales recorded yet</p>
            </Card>
          ) : (
            sales.map((sale) => {
              const saleProfit = sale.total_price - (sale.cost_per_item || 0) * sale.quantity - (sale.discount_amount || 0);
              return (
                <Card key={sale.id} className="bg-card border-border/50">
                  <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold">{sale.product_name || sale.product_type}</span>
                          {sale.size && <span className="text-sm text-muted-foreground">({sale.size})</span>}
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-primary/20 text-primary">
                            {sale.product_type}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground">{getEventName(sale.event_id)}</p>
                        <div className="flex flex-wrap gap-3 text-xs text-muted-foreground mt-1">
                          <span>Qty: {sale.quantity} × {formatCurrency(sale.unit_price)}</span>
                          {sale.discount_amount > 0 && (
                            <span className="text-orange-400 flex items-center gap-1">
                              <Tag className="h-3 w-3" /> -{formatCurrency(sale.discount_amount)} discount
                            </span>
                          )}
                          {sale.cost_per_item > 0 && (
                            <span className="text-red-400">COGS: {formatCurrency(sale.cost_per_item)}/ea</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-lg font-bold text-primary font-mono">{formatCurrency(sale.total_price)}</p>
                          {sale.cost_per_item > 0 && (
                            <p className={`text-xs ${saleProfit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              Profit: {formatCurrency(saleProfit)}
                            </p>
                          )}
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(sale)}>
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" className="text-destructive" onClick={() => handleDelete(sale.id)}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>
      ) : activeTab === 'analytics' ? (
        /* Analytics */
        <div className="space-y-4">
          {/* Summary Cards - Enhanced */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <Card className="bg-card border-border/50">
              <CardContent className="p-4 text-center">
                <p className="text-xs text-muted-foreground">Total Sales</p>
                <p className="text-2xl font-bold">{analytics?.total_quantity || 0}</p>
              </CardContent>
            </Card>
            <Card className="bg-card border-border/50">
              <CardContent className="p-4 text-center">
                <p className="text-xs text-muted-foreground">Revenue</p>
                <p className="text-2xl font-bold text-green-500">{formatCurrency(analytics?.total_revenue)}</p>
              </CardContent>
            </Card>
            <Card className="bg-card border-border/50">
              <CardContent className="p-4 text-center">
                <p className="text-xs text-muted-foreground">COGS</p>
                <p className="text-2xl font-bold text-red-400">{formatCurrency(analytics?.total_cogs)}</p>
              </CardContent>
            </Card>
            <Card className="bg-card border-border/50">
              <CardContent className="p-4 text-center">
                <p className="text-xs text-muted-foreground">Discounts Given</p>
                <p className="text-2xl font-bold text-orange-400">{formatCurrency(analytics?.total_discounts)}</p>
              </CardContent>
            </Card>
            <Card className="bg-card border-border/50">
              <CardContent className="p-4 text-center">
                <p className="text-xs text-muted-foreground">Gross Profit</p>
                <p className={`text-2xl font-bold ${(analytics?.gross_profit || 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {formatCurrency(analytics?.gross_profit)}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-card border-border/50">
              <CardContent className="p-4 text-center">
                <p className="text-xs text-muted-foreground">Profit Margin</p>
                <p className={`text-2xl font-bold ${(analytics?.profit_margin_percentage || 0) >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  {analytics?.profit_margin_percentage || 0}%
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Net Profit & ROI */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Card className="bg-green-500/10 border-green-500/30">
              <CardContent className="p-4 flex justify-between items-center">
                <div>
                  <p className="text-sm text-muted-foreground">Net Profit</p>
                  <p className="text-xs text-muted-foreground">(After expenses)</p>
                </div>
                <p className={`text-3xl font-bold ${(analytics?.net_profit || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {formatCurrency(analytics?.net_profit)}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-primary/10 border-primary/30">
              <CardContent className="p-4 flex justify-between items-center">
                <div>
                  <p className="text-sm text-muted-foreground">ROI</p>
                  <p className="text-xs text-muted-foreground">(Return on Investment)</p>
                </div>
                <p className={`text-3xl font-bold ${(analytics?.roi_percentage || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {analytics?.roi_percentage || 0}%
                </p>
              </CardContent>
            </Card>
          </div>

          {/* By Product Type - Enhanced */}
          <Card className="bg-card border-border/50">
            <CardHeader><CardTitle className="text-lg">Sales by Product Type</CardTitle></CardHeader>
            <CardContent>
              {analytics?.by_product_type && Object.entries(analytics.by_product_type).length > 0 ? (
                <div className="space-y-3">
                  {Object.entries(analytics.by_product_type).map(([type, data]) => {
                    const profit = data.revenue - data.cogs - data.discounts;
                    return (
                      <div key={type} className="p-3 bg-secondary/30 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">{type}</p>
                            <p className="text-sm text-muted-foreground">{data.quantity} sold</p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-primary">{formatCurrency(data.revenue)}</p>
                            <p className={`text-xs ${profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                              Profit: {formatCurrency(profit)}
                            </p>
                          </div>
                        </div>
                        {(data.cogs > 0 || data.discounts > 0) && (
                          <div className="flex gap-4 text-xs text-muted-foreground mt-2 pt-2 border-t border-border/30">
                            {data.cogs > 0 && <span>COGS: {formatCurrency(data.cogs)}</span>}
                            {data.discounts > 0 && <span>Discounts: {formatCurrency(data.discounts)}</span>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-4">No data</p>
              )}
            </CardContent>
          </Card>
        </div>
      ) : (
        /* Top Sellers Tab */
        <div className="space-y-4">
          {/* Top Sellers by Quantity */}
          <Card className="bg-card border-border/50">
            <CardHeader><CardTitle className="text-lg flex items-center gap-2"><TrendingUp className="h-5 w-5 text-primary" /> Top Sellers by Quantity</CardTitle></CardHeader>
            <CardContent>
              {analytics?.top_sellers && analytics.top_sellers.length > 0 ? (
                <div className="space-y-2">
                  {analytics.top_sellers.map((item, idx) => (
                    <div key={item.name} className="flex items-center justify-between p-3 bg-secondary/20 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className={`w-8 h-8 flex items-center justify-center rounded-full font-bold ${idx < 3 ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground'}`}>
                          {idx + 1}
                        </span>
                        <div>
                          <p className="font-medium">{item.name}</p>
                          <p className="text-xs text-muted-foreground">{item.quantity} units sold</p>
                        </div>
                      </div>
                      <p className="font-mono font-bold text-green-400">{formatCurrency(item.revenue)}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-4">No sales data yet</p>
              )}
            </CardContent>
          </Card>

          {/* Top Revenue Generators */}
          <Card className="bg-card border-border/50">
            <CardHeader><CardTitle className="text-lg flex items-center gap-2"><DollarSign className="h-5 w-5 text-green-400" /> Top Revenue Generators</CardTitle></CardHeader>
            <CardContent>
              {analytics?.top_revenue && analytics.top_revenue.length > 0 ? (
                <div className="space-y-2">
                  {analytics.top_revenue.map((item, idx) => (
                    <div key={item.name} className="flex items-center justify-between p-3 bg-secondary/20 rounded-lg">
                      <div className="flex items-center gap-3">
                        <span className={`w-8 h-8 flex items-center justify-center rounded-full font-bold ${idx < 3 ? 'bg-green-500 text-white' : 'bg-secondary text-muted-foreground'}`}>
                          {idx + 1}
                        </span>
                        <div>
                          <p className="font-medium">{item.name}</p>
                          <p className="text-xs text-muted-foreground">{item.quantity} units</p>
                        </div>
                      </div>
                      <p className="font-mono font-bold text-green-400">{formatCurrency(item.revenue)}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-4">No sales data yet</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto bg-card">
            <CardHeader className="border-b border-border flex-row items-center justify-between">
              <CardTitle>{editingSale ? 'Edit Sale' : 'Record Sale'}</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => { setShowForm(false); setEditingSale(null); }}>
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="p-4">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Event *</label>
                  <select
                    value={formData.event_id}
                    onChange={(e) => setFormData({...formData, event_id: e.target.value})}
                    className="w-full px-3 py-2 rounded-lg bg-secondary/50 border border-transparent focus:border-primary text-foreground"
                    required
                  >
                    <option value="">Select Event</option>
                    {events.map(event => (
                      <option key={event.id} value={event.id}>{event.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Product Type *</label>
                  <select
                    value={formData.product_type}
                    onChange={(e) => handleProductTypeChange(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-secondary/50 border border-transparent focus:border-primary text-foreground"
                    required
                  >
                    <option value="">Select Type</option>
                    {PRODUCT_TYPES.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Product Name/Design</label>
                  <Input
                    value={formData.product_name}
                    onChange={(e) => setFormData({...formData, product_name: e.target.value})}
                    placeholder="e.g., Toga Waifu, Gear 5 Luffy"
                    className="bg-secondary/50 border-transparent focus:border-primary"
                  />
                </div>

                {(formData.product_type === 'Canvas' || formData.product_type === 'Shirt') && (
                  <div>
                    <label className="text-sm text-muted-foreground mb-1 block">Size</label>
                    <select
                      value={formData.size}
                      onChange={(e) => setFormData({...formData, size: e.target.value})}
                      className="w-full px-3 py-2 rounded-lg bg-secondary/50 border border-transparent focus:border-primary text-foreground"
                    >
                      <option value="">Select Size</option>
                      {(formData.product_type === 'Canvas' ? CANVAS_SIZES : SHIRT_SIZES).map(size => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm text-muted-foreground mb-1 block">Quantity *</label>
                    <Input
                      type="number"
                      min="1"
                      value={formData.quantity}
                      onChange={(e) => setFormData({...formData, quantity: e.target.value})}
                      className="bg-secondary/50 border-transparent focus:border-primary"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground mb-1 block">Unit Price *</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.unit_price}
                        onChange={(e) => setFormData({...formData, unit_price: e.target.value})}
                        className="pl-7 bg-secondary/50 border-transparent focus:border-primary"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Cost & Discount Fields */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm text-muted-foreground mb-1 block flex items-center gap-1">
                      <Percent className="h-3 w-3" /> Cost per Item
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.cost_per_item}
                        onChange={(e) => setFormData({...formData, cost_per_item: e.target.value})}
                        placeholder="COGS"
                        className="pl-7 bg-secondary/50 border-transparent focus:border-primary"
                      />
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1">Cost of goods sold</p>
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground mb-1 block flex items-center gap-1">
                      <Tag className="h-3 w-3" /> Discount Given
                    </label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.discount_amount}
                        onChange={(e) => setFormData({...formData, discount_amount: e.target.value})}
                        placeholder="0.00"
                        className="pl-7 bg-secondary/50 border-transparent focus:border-primary"
                      />
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1">Total discount on this sale</p>
                  </div>
                </div>

                {formData.unit_price && formData.quantity && (
                  <div className="p-3 bg-primary/10 rounded-lg space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span>{formatCurrency(parseFloat(formData.unit_price) * parseInt(formData.quantity))}</span>
                    </div>
                    {formData.discount_amount && parseFloat(formData.discount_amount) > 0 && (
                      <div className="flex justify-between text-sm text-orange-400">
                        <span>Discount</span>
                        <span>-{formatCurrency(parseFloat(formData.discount_amount))}</span>
                      </div>
                    )}
                    {formData.cost_per_item && parseFloat(formData.cost_per_item) > 0 && (
                      <div className="flex justify-between text-sm text-red-400">
                        <span>COGS ({formData.quantity} × {formatCurrency(parseFloat(formData.cost_per_item))})</span>
                        <span>-{formatCurrency(parseFloat(formData.cost_per_item) * parseInt(formData.quantity))}</span>
                      </div>
                    )}
                    <div className="flex justify-between pt-2 border-t border-border/50">
                      <span className="font-medium">Net Profit</span>
                      <span className={`text-xl font-bold ${
                        (parseFloat(formData.unit_price) * parseInt(formData.quantity) - 
                        (parseFloat(formData.discount_amount) || 0) - 
                        (parseFloat(formData.cost_per_item) || 0) * parseInt(formData.quantity)) >= 0 
                          ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {formatCurrency(
                          parseFloat(formData.unit_price) * parseInt(formData.quantity) - 
                          (parseFloat(formData.discount_amount) || 0) - 
                          (parseFloat(formData.cost_per_item) || 0) * parseInt(formData.quantity)
                        )}
                      </span>
                    </div>
                  </div>
                )}

                <div>
                  <label className="text-sm text-muted-foreground mb-1 block">Notes</label>
                  <Input
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder="Optional notes"
                    className="bg-secondary/50 border-transparent focus:border-primary"
                  />
                </div>

                <Button type="submit" className="w-full">
                  <Save className="h-4 w-4 mr-2" /> {editingSale ? 'Update Sale' : 'Record Sale'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
